/**
 * API Service for Schedule Recommendations
 * Matches backend routes: backend/src/api/routes/schedule_recommendations.py
 */

import { apiClient } from '@/services/apiClient';
import type {
  WeeklyScheduleRecommendation,
  WeeklyScheduleSummary,
  ScheduleZoneDay,
  ScheduleGenerationRequest,
  ApproveScheduleRequest,
  ScheduleValidationResult,
  ZoneNeed,
  ListRecommendationsParams,
  UpdateScheduleDayParams,
  ScheduleDayUpdateResponse,
  ConfirmPastEventRequest,
  ConfirmPastEventResponse,
} from '@/types/scheduleRecommendations';

class ScheduleRecommendationsApiService {
  private api = apiClient;

  /**
   * Generate a new weekly schedule recommendation
   * POST /schedule-recommendations/farms/{farm_id}/generate
   */
  async generateWeeklyRecommendation(
    farmId: string,
    params: {
      week_start_date?: string;
      zone_id?: number;
      apply_seasonal_allocation?: boolean;
      notes?: string;
    }
  ): Promise<WeeklyScheduleRecommendation> {
    const queryParams = new URLSearchParams();
    if (params.week_start_date) {
      queryParams.append('week_start_date', params.week_start_date);
    }
    if (params.zone_id !== undefined) {
      queryParams.append('zone_id', params.zone_id.toString());
    }
    if (params.apply_seasonal_allocation !== undefined) {
      queryParams.append('apply_seasonal_allocation', params.apply_seasonal_allocation.toString());
    }
    if (params.notes) {
      queryParams.append('notes', params.notes);
    }

    const url = `/schedule-recommendations/farms/${farmId}/generate${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.api.post<WeeklyScheduleRecommendation>(url, {});
  }

  /**
   * List schedule recommendations for a farm
   * GET /schedule-recommendations/farms/{farm_id}/recommendations
   */
  async listRecommendations(
    farmId: string,
    params?: ListRecommendationsParams
  ): Promise<WeeklyScheduleSummary[]> {
    const queryParams = new URLSearchParams();
    if (params?.status_filter) {
      queryParams.append('status', params.status_filter);
    }
    if (params?.week_start) {
      queryParams.append('week_start', params.week_start);
    }
    if (params?.zone_id !== undefined) {
      queryParams.append('zone_id', params.zone_id.toString());
    }
    if (params?.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }

    const url = `/schedule-recommendations/farms/${farmId}/recommendations${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.api.get<WeeklyScheduleSummary[]>(url);
  }

  /**
   * Get recommendation details by ID
   * GET /schedule-recommendations/recommendations/{recommendation_id}
   */
  async getRecommendation(recommendationId: number): Promise<WeeklyScheduleRecommendation> {
    return this.api.get<WeeklyScheduleRecommendation>(`/schedule-recommendations/recommendations/${recommendationId}`);
  }

  /**
   * Update recommendation status
   * PUT /schedule-recommendations/recommendations/{recommendation_id}/status
   */
  async updateRecommendationStatus(
    recommendationId: number,
    status: string
  ): Promise<WeeklyScheduleRecommendation> {
    return this.api.put<WeeklyScheduleRecommendation>(
      `/schedule-recommendations/recommendations/${recommendationId}/status?status=${status}`,
      {}
    );
  }

  /**
   * Delete a schedule recommendation
   * DELETE /schedule-recommendations/recommendations/{recommendation_id}
   */
  async deleteRecommendation(recommendationId: number): Promise<{ message: string }> {
    return this.api.delete<{ message: string }>(`/schedule-recommendations/recommendations/${recommendationId}`);
  }

  /**
   * Get all schedule days for a recommendation
   * GET /schedule-recommendations/recommendations/{recommendation_id}/days
   */
  async getScheduleDays(recommendationId: number): Promise<ScheduleZoneDay[]> {
    return this.api.get<ScheduleZoneDay[]>(
      `/schedule-recommendations/recommendations/${recommendationId}/days`
    );
  }

  /**
   * Update a schedule day
   * PUT /schedule-recommendations/recommendations/{recommendation_id}/days/{day_id}
   */
  async updateScheduleDay(
    recommendationId: number,
    dayId: number,
    params: UpdateScheduleDayParams
  ): Promise<ScheduleDayUpdateResponse> {
    const queryParams = new URLSearchParams();
    if (params.start_time) {
      queryParams.append('start_time', params.start_time);
    }
    if (params.duration_minutes !== undefined) {
      queryParams.append('duration_minutes', params.duration_minutes.toString());
    }
    if (params.planned_water_mm !== undefined) {
      queryParams.append('planned_water_mm', params.planned_water_mm.toString());
    }
    if (params.priority !== undefined) {
      queryParams.append('priority', params.priority.toString());
    }
    if (params.status) {
      queryParams.append('status', params.status);
    }

    const url = `/schedule-recommendations/recommendations/${recommendationId}/days/${dayId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.api.put<ScheduleDayUpdateResponse>(url, {});
  }

  /**
   * Update a schedule day with conflict detection
   * PUT /schedule-recommendations/recommendations/{recommendation_id}/days/{day_id}
   */
  async updateScheduleDayWithConflicts(
    recommendationId: number,
    dayId: number,
    params: UpdateScheduleDayParams
  ): Promise<ScheduleDayUpdateResponse> {
    const queryParams = new URLSearchParams();
    if (params.start_time) {
      queryParams.append('start_time', params.start_time);
    }
    if (params.duration_minutes !== undefined) {
      queryParams.append('duration_minutes', params.duration_minutes.toString());
    }
    if (params.planned_water_mm !== undefined) {
      queryParams.append('planned_water_mm', params.planned_water_mm.toString());
    }
    if (params.priority !== undefined) {
      queryParams.append('priority', params.priority.toString());
    }
    if (params.status) {
      queryParams.append('status', params.status);
    }

    const url = `/schedule-recommendations/recommendations/${recommendationId}/days/${dayId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.api.put<ScheduleDayUpdateResponse>(url, {});
  }

  /**
   * Apply time shift to affected events
   * PUT /schedule-recommendations/recommendations/{recommendation_id}/days/{day_id}/apply-time-shift
   */
  async applyTimeShift(
    recommendationId: number,
    dayId: number,
    shiftMinutes: number
  ): Promise<ScheduleZoneDay[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('shift_minutes', shiftMinutes.toString());

    const url = `/schedule-recommendations/recommendations/${recommendationId}/days/${dayId}/apply-time-shift${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.api.put<ScheduleZoneDay[]>(url, {});
  }

  /**
   * Delete a schedule day
   * DELETE /schedule-recommendations/recommendations/{recommendation_id}/days/{day_id}
   */
  async deleteScheduleDay(
    recommendationId: number,
    dayId: number
  ): Promise<{ message: string }> {
    return this.api.delete<{ message: string }>(
      `/schedule-recommendations/recommendations/${recommendationId}/days/${dayId}`
    );
  }

  /**
   * Validate a schedule against constraints
   * POST /schedule-recommendations/validate
   */
  async validateSchedule(scheduleData: any): Promise<ScheduleValidationResult> {
    return this.api.post<ScheduleValidationResult>('/schedule-recommendations/validate', scheduleData);
  }

  /**
   * Get current irrigation needs for all zones in a farm
   * GET /schedule-recommendations/farms/{farm_id}/zone-needs
   */
  async getZoneNeeds(
    farmId: string,
    week_start?: string
  ): Promise<ZoneNeed[]> {
    const queryParams = new URLSearchParams();
    if (week_start) {
      queryParams.append('week_start', week_start);
    }

    const url = `/schedule-recommendations/farms/${farmId}/zone-needs${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.api.get<ZoneNeed[]>(url);
  }

  /**
   * Approve a schedule recommendation and create execution records
   * POST /schedule-recommendations/farms/{farm_id}/schedule
   */
  async approveSchedule(
    farmId: string,
    request: ApproveScheduleRequest
  ): Promise<WeeklyScheduleRecommendation> {
    return this.api.post<WeeklyScheduleRecommendation>(
      `/schedule-recommendations/farms/${farmId}/schedule`,
      request
    );
  }

  /**
   * Regenerate an existing weekly schedule recommendation
   * POST /schedule-recommendations/{recommendation_id}/regenerate
   */
  async regenerateWeeklyRecommendation(
    recommendationId: number,
    params: {
      cascade_following_weeks?: boolean;
      notes?: string;
    }
  ): Promise<{
    recommendation_id: number;
    week_start: string;
    week_end: string;
    preserved_days: Array<{
      date: string;
      sector_id: number;
      planned_water_mm: number;
      actual_water_mm: number | null;
      status: string;
    }>;
    regenerated_days: Array<{
      date: string;
      sector_id: number;
      planned_water_mm: number;
      actual_water_mm: number | null;
      status: string;
    }>;
    cascaded_weeks: number[];
    status: string;
    message: string;
  }> {
    const queryParams = new URLSearchParams();

    // Default cascade to true
    const cascadeValue = params.cascade_following_weeks !== undefined ? params.cascade_following_weeks : true;
    queryParams.append('cascade_following_weeks', cascadeValue.toString());

    if (params.notes) {
      queryParams.append('notes', params.notes);
    }

    const url = `/schedule-recommendations/${recommendationId}/regenerate${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    return this.api.post(url, {});
  }

  /**
   * Confirm a past irrigation event and update water balance
   * POST /schedule-recommendations/recommendations/{recommendation_id}/days/{day_id}/confirm
   */
  async confirmPastEvent(
    recommendationId: number,
    dayId: number,
    request: ConfirmPastEventRequest
  ): Promise<ConfirmPastEventResponse> {
    return this.api.post<ConfirmPastEventResponse>(
      `/schedule-recommendations/recommendations/${recommendationId}/days/${dayId}/confirm`,
      request
    );
  }

  /**
   * Confirm past irrigation for all sectors in a zone
   * POST /schedule-recommendations/recommendations/{recommendation_id}/zones/{zone_id}/days/{irrigation_date}/confirm
   */
  async confirmZonePastEvent(
    recommendationId: number,
    zoneId: number,
    irrigationDate: string,
    data: {
      actual_water_mm: number;
      actual_duration_minutes?: number;
      actual_start_time?: string;
      execution_notes?: string;
    }
  ): Promise<{
    schedule_days: ScheduleZoneDay[];
    task_ids: string[];
    confirmed_count: number;
  }> {
    return this.api.post(
      `/schedule-recommendations/recommendations/${recommendationId}/zones/${zoneId}/days/${irrigationDate}/confirm`,
      data
    );
  }

  /**
   * Confirm past irrigation for a single sector
   * POST /schedule-recommendations/recommendations/{recommendation_id}/sectors/{sector_id}/days/{irrigation_date}/confirm
   */
  async confirmSectorPastEvent(
    recommendationId: number,
    sectorId: number,
    irrigationDate: string,
    data: {
      actual_water_mm: number;
      actual_duration_minutes?: number;
      actual_start_time?: string;
      execution_notes?: string;
    }
  ): Promise<{
    schedule_day: ScheduleZoneDay;
    task_id: string;
  }> {
    return this.api.post(
      `/schedule-recommendations/recommendations/${recommendationId}/sectors/${sectorId}/days/${irrigationDate}/confirm`,
      data
    );
  }
}

export default new ScheduleRecommendationsApiService();
